/**
 * @fileoverview Admin System Type Definitions
 * @module shared/types/AdminSystem
 * @version 1.0.0
 * @author Telegamez Admin System
 * 
 * @description
 * Comprehensive TypeScript type definitions for the Admin System Phase 2.
 * Includes role-based access control, session management, and audit logging.
 * These types integrate with the existing avatar companion system and provide
 * secure admin functionality.
 * 
 * @dependencies
 * - zod: Runtime schema validation
 * - Database schema compatibility with packages/shared/db/src/schema.ts
 * - Integration with existing NextAuth authentication
 */

import { z } from 'zod';

// =============================================================================
// ENUMS AND CONSTANTS
// =============================================================================

/**
 * Admin role hierarchy with increasing permissions
 */
export enum AdminRole {
  /** View-only access to admin interface */
  VIEWER = 'viewer',
  /** Can edit content but not manage users */
  EDITOR = 'editor', 
  /** Full admin access except super admin functions */
  ADMIN = 'admin',
  /** Full system access including user management */
  SUPER_ADMIN = 'super_admin'
}

/**
 * Admin permissions for granular access control
 */
export enum AdminPermission {
  // Companion Management
  COMPANION_VIEW = 'companion:view',
  COMPANION_CREATE = 'companion:create',
  COMPANION_EDIT = 'companion:edit',
  COMPANION_DELETE = 'companion:delete',
  COMPANION_PUBLISH = 'companion:publish',
  
  // Asset Management
  ASSET_VIEW = 'asset:view',
  ASSET_UPLOAD = 'asset:upload',
  ASSET_DELETE = 'asset:delete',
  ASSET_MANAGE_CDN = 'asset:manage_cdn',
  
  // Analytics & Reporting
  ANALYTICS_VIEW = 'analytics:view',
  ANALYTICS_EXPORT = 'analytics:export',
  ANALYTICS_ADVANCED = 'analytics:advanced',
  
  // User & Role Management
  USER_VIEW = 'user:view',
  USER_CREATE = 'user:create',
  USER_EDIT = 'user:edit',
  USER_DELETE = 'user:delete',
  ROLE_ASSIGN = 'role:assign',
  
  // System Administration
  SYSTEM_CONFIG = 'system:config',
  SYSTEM_LOGS = 'system:logs',
  SYSTEM_MAINTENANCE = 'system:maintenance',
  
  // Audit & Security
  AUDIT_VIEW = 'audit:view',
  AUDIT_EXPORT = 'audit:export',
  SECURITY_MANAGE = 'security:manage',

  // Inference Testing
  INFERENCE_TEST = 'inference:test',

  // Homepage Content Management
  HOMEPAGE_VIEW = 'homepage:view',
  HOMEPAGE_EDIT = 'homepage:edit',
  HOMEPAGE_PUBLISH = 'homepage:publish',

  // Talk Show Management
  TALK_SHOW_VIEW = 'talk_show:view',
  TALK_SHOW_CREATE = 'talk_show:create',
  TALK_SHOW_EDIT = 'talk_show:edit',
  TALK_SHOW_DELETE = 'talk_show:delete',

  // Talk Show Theme Management
  THEME_VIEW = 'theme:view',
  THEME_CREATE = 'theme:create',
  THEME_EDIT = 'theme:edit',
  THEME_DELETE = 'theme:delete',

  // TTS Pronunciation Dictionary
  TTS_PRONUNCIATION_VIEW = 'tts_pronunciation:view',
  TTS_PRONUNCIATION_CREATE = 'tts_pronunciation:create',
  TTS_PRONUNCIATION_EDIT = 'tts_pronunciation:edit',
  TTS_PRONUNCIATION_DELETE = 'tts_pronunciation:delete',
  MUSIC_VIEW = 'music:view',
  MUSIC_CREATE = 'music:create',
  MUSIC_EDIT = 'music:edit',
  MUSIC_DELETE = 'music:delete',

  // Web Games catalog (#4495)
  WEB_GAMES_VIEW = 'web_games:view',
  WEB_GAMES_CREATE = 'web_games:create',
  WEB_GAMES_EDIT = 'web_games:edit'
}

/**
 * Login methods for admin authentication
 */
export enum AdminLoginMethod {
  GOOGLE = 'google',
  MANUAL = 'manual'
}

/**
 * Admin activity action types
 */
export enum AdminActivityAction {
  // Authentication
  LOGIN = 'auth:login',
  LOGOUT = 'auth:logout',
  SESSION_REFRESH = 'auth:session_refresh',
  
  // Companion Management
  COMPANION_VIEW = 'companion:view',
  COMPANION_CREATE = 'companion:create',
  COMPANION_UPDATE = 'companion:update',
  COMPANION_DELETE = 'companion:delete',
  COMPANION_ACTIVATE = 'companion:activate',
  COMPANION_DEACTIVATE = 'companion:deactivate',
  
  // Asset Management
  ASSET_UPLOAD = 'asset:upload',
  ASSET_DELETE = 'asset:delete',
  ASSET_CDN_SYNC = 'asset:cdn_sync',
  
  // Avatar Management
  AVATAR_VIEW = 'avatar:view',
  AVATAR_CREATE = 'avatar:create',
  AVATAR_UPDATE = 'avatar:update',
  AVATAR_DELETE = 'avatar:delete',
  AVATAR_ASSET_UPLOAD = 'avatar:asset_upload',
  AVATAR_ASSET_DELETE = 'avatar:asset_delete',
  
  // User Management
  USER_CREATE = 'user:create',
  USER_UPDATE = 'user:update',
  USER_ROLE_CHANGE = 'user:role_change',
  USER_DEACTIVATE = 'user:deactivate',
  
  // System Actions
  SYSTEM_CONFIG_CHANGE = 'system:config_change',
  CACHE_CLEAR = 'system:cache_clear',
  CHANNELS_REFRESH = 'system:channels_refresh',
  
  // Data Export
  DATA_EXPORT = 'data:export',
  AUDIT_EXPORT = 'audit:export',

  // Inference Testing
  INFERENCE_TEST = 'inference:test',

  // Homepage Content
  HOMEPAGE_HERO_UPDATE = 'homepage:hero_update',
  HOMEPAGE_STORY_CREATE = 'homepage:story_create',
  HOMEPAGE_STORY_UPDATE = 'homepage:story_update',
  HOMEPAGE_STORY_DELETE = 'homepage:story_delete',
  HOMEPAGE_STORY_REORDER = 'homepage:story_reorder',
  HOMEPAGE_PUBLISH = 'homepage:publish',

  // Talk Show Management
  TALK_SHOW_LIST = 'talk_show:list',
  TALK_SHOW_CREATE = 'talk_show:create',
  TALK_SHOW_UPDATE = 'talk_show:update',
  TALK_SHOW_DELETE = 'talk_show:delete',
  TALK_SHOW_START = 'talk_show:start',
  TALK_SHOW_STOP = 'talk_show:stop',
  TALK_SHOW_COMPANION_ADD = 'talk_show:companion_add',
  TALK_SHOW_COMPANION_UPDATE = 'talk_show:companion_update',
  TALK_SHOW_COMPANION_REMOVE = 'talk_show:companion_remove',

  // Talk Show Theme Management
  THEME_LIST = 'theme:list',
  THEME_CREATE = 'theme:create',
  THEME_UPDATE = 'theme:update',
  THEME_DELETE = 'theme:delete',

  // TTS Pronunciation Dictionary
  TTS_PRONUNCIATION_LIST = 'tts_pronunciation:list',
  TTS_PRONUNCIATION_CREATE = 'tts_pronunciation:create',
  TTS_PRONUNCIATION_UPDATE = 'tts_pronunciation:update',
  TTS_PRONUNCIATION_DELETE = 'tts_pronunciation:delete',
  MUSIC_LIST = 'music:list',
  MUSIC_SYNC_VIEW = 'music:sync_view',
  MUSIC_CREATE = 'music:create',
  MUSIC_UPDATE = 'music:update',
  MUSIC_DELETE = 'music:delete',
  MUSIC_UPLOAD = 'music:upload',
  TTS_PRONUNCIATION_REFRESH = 'tts_pronunciation:refresh',
  WEB_GAME_LIST = 'web_game:list',
  WEB_GAME_CREATE = 'web_game:create',
  WEB_GAME_UPDATE = 'web_game:update',
  WEB_GAME_CHECK = 'web_game:check',
  WEB_GAME_IMPORT = 'web_game:import',
  WEB_GAME_KEY_CREATE = 'web_game:key_create',
  WEB_GAME_KEY_REVOKE = 'web_game:key_revoke',
  WEB_GAME_SUBMISSION_APPROVE = 'web_game:submission_approve',
  WEB_GAME_SUBMISSION_REJECT = 'web_game:submission_reject'
}

/**
 * Resources that can be acted upon
 */
export enum AdminResource {
  COMPANION = 'avatar_companion',
  ASSET = 'asset',
  USER = 'admin_user',
  SESSION = 'admin_session',
  SYSTEM = 'system',
  AUDIT = 'audit_log',
  INFERENCE = 'inference',
  HOMEPAGE = 'homepage_content',
  TALK_SHOW = 'talk_show',
  TALK_SHOW_THEME = 'talk_show_theme',
  MUSIC_PLAYLIST = 'music_playlist',
  MUSIC_TRACK = 'music_track',
  MUSIC_ROOM = 'music_room',
  TTS_PRONUNCIATION = 'tts_pronunciation',
  WEB_GAME = 'web_game',
  WEB_GAME_API_KEY = 'web_game_api_key',
  WEB_GAME_SUBMISSION = 'web_game_submission'
}

// =============================================================================
// VALIDATION SCHEMAS
// =============================================================================

/**
 * Admin user schema for validation
 */
export const AdminUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().refine(email => email.endsWith('@telegamez.com'), {
    message: 'Email must be from @telegamez.com domain'
  }),
  role: z.nativeEnum(AdminRole),
  isActive: z.boolean(),
  lastLoginAt: z.date().nullable(),
  loginCount: z.number().int().min(0),
  permissions: z.array(z.nativeEnum(AdminPermission)),
  assignedBy: z.string().email(),
  assignedAt: z.date(),
  revokedAt: z.date().nullable(),
  revokedBy: z.string().nullable(),
  revokedReason: z.string().nullable(),
  displayName: z.string().nullable(),
  department: z.string().nullable(),
  jobTitle: z.string().nullable(),
  preferences: z.object({
    emailNotifications: z.boolean().optional(),
    theme: z.enum(['light', 'dark', 'system']).optional(),
    timezone: z.string().optional(),
    language: z.string().optional(),
  }).optional(),
  createdAt: z.date(),
  updatedAt: z.date()
});

/**
 * New admin user schema for creation
 */
export const NewAdminUserSchema = AdminUserSchema.omit({
  id: true,
  lastLoginAt: true,
  loginCount: true,
  revokedAt: true,
  revokedBy: true,
  revokedReason: true,
  createdAt: true,
  updatedAt: true
}).extend({
  assignedBy: z.string().email().refine(email => email.endsWith('@telegamez.com'))
});

/**
 * Admin session schema for validation
 */
export const AdminSessionSchema = z.object({
  id: z.string().uuid(),
  adminUserId: z.string().uuid(),
  sessionToken: z.string().min(1),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  location: z.string().nullable(),
  loginMethod: z.nativeEnum(AdminLoginMethod),
  isActive: z.boolean(),
  expiresAt: z.date(),
  revokedAt: z.date().nullable(),
  revokedReason: z.string().nullable(),
  createdAt: z.date(),
  lastActivityAt: z.date()
});

/**
 * Admin activity log schema for validation
 */
export const AdminActivityLogSchema = z.object({
  id: z.string().uuid(),
  adminUserId: z.string().uuid().nullable(),
  sessionId: z.string().uuid().nullable(),
  action: z.nativeEnum(AdminActivityAction),
  resource: z.nativeEnum(AdminResource),
  resourceId: z.string().nullable(),
  details: z.record(z.any()).nullable(),
  oldValues: z.record(z.any()).nullable(),
  newValues: z.record(z.any()).nullable(),
  ipAddress: z.string().nullable(),
  userAgent: z.string().nullable(),
  success: z.boolean(),
  errorMessage: z.string().nullable(),
  duration: z.number().int().min(0).nullable(),
  createdAt: z.date()
});

// =============================================================================
// DATABASE ENTITY TYPES
// =============================================================================

export type AdminUser = z.infer<typeof AdminUserSchema>;
export type NewAdminUser = z.infer<typeof NewAdminUserSchema>;
export type AdminSession = z.infer<typeof AdminSessionSchema>;
export type NewAdminSession = Omit<AdminSession, 'id' | 'createdAt' | 'lastActivityAt'>;
export type AdminActivityLog = z.infer<typeof AdminActivityLogSchema>;

// =============================================================================
// API REQUEST/RESPONSE SCHEMAS
// =============================================================================

/**
 * Admin authentication request
 */
export const AdminAuthRequestSchema = z.object({
  sessionToken: z.string().min(1),
  userAgent: z.string().optional(),
  ipAddress: z.string().optional()
});

/**
 * Admin authentication response
 */
export const AdminAuthResponseSchema = z.object({
  success: z.boolean(),
  user: AdminUserSchema.optional(),
  session: AdminSessionSchema.pick({
    id: true,
    expiresAt: true,
    lastActivityAt: true
  }).optional(),
  permissions: z.array(z.nativeEnum(AdminPermission)).optional(),
  error: z.object({
    message: z.string(),
    code: z.string()
  }).optional()
});

/**
 * Admin user creation request
 */
export const AdminUserCreateRequestSchema = z.object({
  email: z.string().email().refine(email => email.endsWith('@telegamez.com')),
  role: z.nativeEnum(AdminRole),
  displayName: z.string().optional(),
  department: z.string().optional(),
  jobTitle: z.string().optional(),
  permissions: z.array(z.nativeEnum(AdminPermission)).optional(),
  reason: z.string().optional()
});

/**
 * Admin user update request
 */
export const AdminUserUpdateRequestSchema = z.object({
  role: z.nativeEnum(AdminRole).optional(),
  isActive: z.boolean().optional(),
  displayName: z.string().nullable().optional(),
  department: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  permissions: z.array(z.nativeEnum(AdminPermission)).optional(),
  preferences: z.object({
    emailNotifications: z.boolean().optional(),
    theme: z.enum(['light', 'dark', 'system']).optional(),
    timezone: z.string().optional(),
    language: z.string().optional(),
  }).optional(),
  reason: z.string().optional()
});

/**
 * Admin activity log query parameters
 */
export const AdminActivityLogQuerySchema = z.object({
  userId: z.string().uuid().optional(),
  action: z.nativeEnum(AdminActivityAction).optional(),
  resource: z.nativeEnum(AdminResource).optional(),
  startDate: z.string().datetime().optional(),
  endDate: z.string().datetime().optional(),
  success: z.boolean().optional(),
  page: z.number().int().min(1).optional(),
  limit: z.number().int().min(1).max(100).optional()
});

// =============================================================================
// API REQUEST/RESPONSE TYPES
// =============================================================================

export type AdminAuthRequest = z.infer<typeof AdminAuthRequestSchema>;
export type AdminAuthResponse = z.infer<typeof AdminAuthResponseSchema>;
export type AdminUserCreateRequest = z.infer<typeof AdminUserCreateRequestSchema>;
export type AdminUserUpdateRequest = z.infer<typeof AdminUserUpdateRequestSchema>;
export type AdminActivityLogQuery = z.infer<typeof AdminActivityLogQuerySchema>;

// =============================================================================
// PERMISSION UTILITIES
// =============================================================================

/**
 * Default permissions for each admin role
 */
const VIEWER_PERMISSIONS = [
  AdminPermission.MUSIC_VIEW,
  AdminPermission.WEB_GAMES_VIEW,
  AdminPermission.COMPANION_VIEW,
  AdminPermission.ASSET_VIEW,
  AdminPermission.ANALYTICS_VIEW
];

const EDITOR_PERMISSIONS = [
  ...VIEWER_PERMISSIONS,
  AdminPermission.MUSIC_CREATE,
  AdminPermission.MUSIC_EDIT,
  AdminPermission.WEB_GAMES_CREATE,
  AdminPermission.WEB_GAMES_EDIT,
  AdminPermission.COMPANION_CREATE,
  AdminPermission.COMPANION_EDIT,
  AdminPermission.ASSET_UPLOAD,
  AdminPermission.ANALYTICS_EXPORT,
  AdminPermission.USER_VIEW, // Allow editors to view user lists for collaboration
  AdminPermission.AUDIT_VIEW, // Allow editors to view audit logs
  AdminPermission.INFERENCE_TEST, // Allow editors to use inference lab
  AdminPermission.HOMEPAGE_VIEW, // Allow editors to view homepage content
  AdminPermission.HOMEPAGE_EDIT, // Allow editors to manage homepage content
  AdminPermission.HOMEPAGE_PUBLISH, // Allow editors to publish homepage content
  AdminPermission.TALK_SHOW_VIEW, // Allow editors to view talk shows
  AdminPermission.TALK_SHOW_CREATE, // Allow editors to create talk shows
  AdminPermission.TALK_SHOW_EDIT, // Allow editors to edit talk shows
  AdminPermission.THEME_VIEW, // Allow editors to view talk show themes
  AdminPermission.THEME_CREATE, // Allow editors to create themes
  AdminPermission.THEME_EDIT, // Allow editors to edit themes
  AdminPermission.TTS_PRONUNCIATION_VIEW,
  AdminPermission.TTS_PRONUNCIATION_CREATE,
  AdminPermission.TTS_PRONUNCIATION_EDIT
];

const ADMIN_PERMISSIONS = [
  ...EDITOR_PERMISSIONS,
  AdminPermission.MUSIC_DELETE,
  AdminPermission.COMPANION_DELETE,
  AdminPermission.COMPANION_PUBLISH,
  AdminPermission.ASSET_DELETE,
  AdminPermission.ASSET_MANAGE_CDN,
  AdminPermission.ANALYTICS_ADVANCED,
  AdminPermission.USER_VIEW,
  AdminPermission.AUDIT_VIEW,
  AdminPermission.SYSTEM_LOGS,
  AdminPermission.HOMEPAGE_VIEW,
  AdminPermission.HOMEPAGE_EDIT,
  AdminPermission.HOMEPAGE_PUBLISH,
  AdminPermission.TALK_SHOW_VIEW,
  AdminPermission.TALK_SHOW_CREATE,
  AdminPermission.TALK_SHOW_EDIT,
  AdminPermission.TALK_SHOW_DELETE,
  AdminPermission.THEME_VIEW,
  AdminPermission.THEME_CREATE,
  AdminPermission.THEME_EDIT,
  AdminPermission.THEME_DELETE,
  AdminPermission.TTS_PRONUNCIATION_VIEW,
  AdminPermission.TTS_PRONUNCIATION_CREATE,
  AdminPermission.TTS_PRONUNCIATION_EDIT,
  AdminPermission.TTS_PRONUNCIATION_DELETE
];

const SUPER_ADMIN_PERMISSIONS = [
  ...ADMIN_PERMISSIONS,
  AdminPermission.USER_CREATE,
  AdminPermission.USER_EDIT,
  AdminPermission.USER_DELETE,
  AdminPermission.ROLE_ASSIGN,
  AdminPermission.SYSTEM_CONFIG,
  AdminPermission.SYSTEM_MAINTENANCE,
  AdminPermission.AUDIT_EXPORT,
  AdminPermission.SECURITY_MANAGE
];

export const DEFAULT_ROLE_PERMISSIONS: Record<AdminRole, AdminPermission[]> = {
  [AdminRole.VIEWER]: VIEWER_PERMISSIONS,
  [AdminRole.EDITOR]: EDITOR_PERMISSIONS,
  [AdminRole.ADMIN]: ADMIN_PERMISSIONS,
  [AdminRole.SUPER_ADMIN]: SUPER_ADMIN_PERMISSIONS
};

/**
 * Check if a user has a specific permission
 */
export function hasPermission(user: AdminUser, permission: AdminPermission): boolean {
  if (!user.isActive) return false;
  return user.permissions.includes(permission);
}

/**
 * Check if a user has any of the specified permissions
 */
export function hasAnyPermission(user: AdminUser, permissions: AdminPermission[]): boolean {
  if (!user.isActive) return false;
  return permissions.some(permission => user.permissions.includes(permission));
}

/**
 * Check if a user has all of the specified permissions
 */
export function hasAllPermissions(user: AdminUser, permissions: AdminPermission[]): boolean {
  if (!user.isActive) return false;
  return permissions.every(permission => user.permissions.includes(permission));
}

/**
 * Get effective permissions for a role (including default permissions)
 */
export function getEffectivePermissions(role: AdminRole, customPermissions: AdminPermission[] = []): AdminPermission[] {
  const defaultPermissions = DEFAULT_ROLE_PERMISSIONS[role] || [];
  const allPermissions = [...defaultPermissions, ...customPermissions];
  return Array.from(new Set(allPermissions)); // Remove duplicates
}

/**
 * Check if a role can perform an action on a resource
 */
export function canPerformAction(
  user: AdminUser, 
  action: AdminActivityAction, 
  resource: AdminResource
): boolean {
  if (!user.isActive) return false;

  // Define action-permission mappings
  const actionPermissionMap: Record<string, AdminPermission[]> = {
    [`${AdminActivityAction.COMPANION_CREATE}`]: [AdminPermission.COMPANION_CREATE],
    [`${AdminActivityAction.COMPANION_UPDATE}`]: [AdminPermission.COMPANION_EDIT],
    [`${AdminActivityAction.COMPANION_DELETE}`]: [AdminPermission.COMPANION_DELETE],
    [`${AdminActivityAction.ASSET_UPLOAD}`]: [AdminPermission.ASSET_UPLOAD],
    [`${AdminActivityAction.ASSET_DELETE}`]: [AdminPermission.ASSET_DELETE],
    [`${AdminActivityAction.USER_CREATE}`]: [AdminPermission.USER_CREATE],
    [`${AdminActivityAction.USER_UPDATE}`]: [AdminPermission.USER_EDIT],
    [`${AdminActivityAction.DATA_EXPORT}`]: [AdminPermission.ANALYTICS_EXPORT],
  };

  const requiredPermissions = actionPermissionMap[action] || [];
  return hasAnyPermission(user, requiredPermissions);
}

// =============================================================================
// AUDIT HELPERS
// =============================================================================

/**
 * Create audit log entry data
 */
export function createAuditLogData({
  adminUserId,
  sessionId,
  action,
  resource,
  resourceId,
  details,
  oldValues,
  newValues,
  ipAddress,
  userAgent,
  success = true,
  errorMessage,
  duration
}: {
  adminUserId?: string;
  sessionId?: string;
  action: AdminActivityAction;
  resource: AdminResource;
  resourceId?: string;
  details?: Record<string, any>;
  oldValues?: Record<string, any>;
  newValues?: Record<string, any>;
  ipAddress?: string;
  userAgent?: string;
  success?: boolean;
  errorMessage?: string;
  duration?: number;
}) {
  return {
    adminUserId: adminUserId || null,
    sessionId: sessionId || null,
    action,
    resource,
    resourceId: resourceId || null,
    details: details || null,
    oldValues: oldValues || null,
    newValues: newValues || null,
    ipAddress: ipAddress || null,
    userAgent: userAgent || null,
    success,
    errorMessage: errorMessage || null,
    duration: duration || null
  };
}

// =============================================================================
// TYPE GUARDS
// =============================================================================

/**
 * Type guard to check if an object is a valid AdminUser
 */
export function isAdminUser(obj: unknown): obj is AdminUser {
  return AdminUserSchema.safeParse(obj).success;
}

/**
 * Type guard to check if a string is a valid AdminRole
 */
export function isValidAdminRole(role: string): role is AdminRole {
  return Object.values(AdminRole).includes(role as AdminRole);
}

/**
 * Type guard to check if a string is a valid AdminPermission
 */
export function isValidAdminPermission(permission: string): permission is AdminPermission {
  return Object.values(AdminPermission).includes(permission as AdminPermission);
}

// =============================================================================
// FRONTEND SPECIFIC TYPES
// =============================================================================

/**
 * Admin user data optimized for API responses
 */
export interface AdminUserAPI {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  loginCount: number;
  permissions: string[];
  displayName: string | null;
  department: string | null;
  jobTitle: string | null;
  assignedBy: string;
  assignedAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Admin session data for frontend
 */
export interface AdminSessionAPI {
  id: string;
  adminUserId: string;
  isActive: boolean;
  expiresAt: string;
  lastActivityAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  location: string | null;
  loginMethod: string;
}

/**
 * Admin activity log data for frontend
 */
export interface AdminActivityLogAPI {
  id: string;
  adminUserId: string | null;
  sessionId: string | null;
  action: string;
  resource: string;
  resourceId: string | null;
  details: Record<string, any> | null;
  success: boolean;
  errorMessage: string | null;
  duration: number | null;
  createdAt: string;
  adminUser?: {
    email: string;
    displayName: string | null;
  };
}

/**
 * Convert AdminUser to API format
 */
export function toAdminUserAPI(user: AdminUser): AdminUserAPI {
  return {
    id: user.id,
    email: user.email,
    role: user.role,
    isActive: user.isActive,
    lastLoginAt: user.lastLoginAt?.toISOString() || null,
    loginCount: user.loginCount,
    permissions: user.permissions,
    displayName: user.displayName,
    department: user.department,
    jobTitle: user.jobTitle,
    assignedBy: user.assignedBy,
    assignedAt: user.assignedAt.toISOString(),
    createdAt: user.createdAt.toISOString(),
    updatedAt: user.updatedAt.toISOString()
  };
}

/**
 * Convert AdminSession to API format
 */
export function toAdminSessionAPI(session: AdminSession): AdminSessionAPI {
  return {
    id: session.id,
    adminUserId: session.adminUserId,
    isActive: session.isActive,
    expiresAt: session.expiresAt.toISOString(),
    lastActivityAt: session.lastActivityAt.toISOString(),
    ipAddress: session.ipAddress,
    userAgent: session.userAgent,
    location: session.location,
    loginMethod: session.loginMethod
  };
}
